import React, { useEffect, useMemo, useState } from "react";
import api from "../api";
import {
  HiOutlineDocumentChartBar,
  HiOutlineArrowDownTray,
  HiOutlineFunnel
} from "react-icons/hi2";

const theme = {
  primary: "#0F766E",
  primaryDark: "#115E59",
  primaryLight: "#14B8A6",
  accent: "#2DD4BF",
  background: "#F8FAFC",
  surface: "#FFFFFF",
  surfaceSoft: "#ECFEFF",
  text: "#0F172A",
  textMuted: "#64748B",
  border: "#CCFBF1"
};

function ManagerReports({ user }) {
  const [data, setData] = useState([]);
  const [filtered, setFiltered] = useState([]);

  const [users, setUsers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [practices, setPractices] = useState([]);

  const [selectedUser, setSelectedUser] = useState("");
  const [task, setTask] = useState("");
  const [practice, setPractice] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [loading, setLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(20);

  const DAILY_TARGET = 40;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      const [prodRes, tasksRes, practicesRes] = await Promise.all([
        api.get("/production"),
        api.get("/tasks"),
        api.get("/practices")
      ]);

      const rows = prodRes.data || [];
      const sortedRows = [...rows].sort(
        (a, b) => new Date(b.date) - new Date(a.date) || Number(b.id || 0) - Number(a.id || 0)
      );

      setData(sortedRows);
      setFiltered(sortedRows);
      setVisibleCount(20);

      const uniqueUsers = [
        ...new Set(sortedRows.map((d) => String(d.user || "").trim()).filter(Boolean))
      ].sort((a, b) => a.localeCompare(b));

      setUsers(uniqueUsers);
      setTasks(tasksRes.data || []);
      setPractices(practicesRes.data || []);
    } catch (err) {
      console.error("Manager reports load failed:", err);
      setData([]);
      setFiltered([]);
      setUsers([]);
      setTasks([]);
      setPractices([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let temp = [...data];

    if (selectedUser) {
      temp = temp.filter((r) => String(r.user || "") === selectedUser);
    }

    if (task) {
      temp = temp.filter((r) => String(r.task || "") === task);
    }

    if (practice) {
      temp = temp.filter((r) => String(r.practice || "") === practice);
    }

    if (fromDate) {
      const start = new Date(fromDate);
      start.setHours(0, 0, 0, 0);
      temp = temp.filter((r) => new Date(r.date) >= start);
    }

    if (toDate) {
      const end = new Date(toDate);
      end.setHours(23, 59, 59, 999);
      temp = temp.filter((r) => new Date(r.date) <= end);
    }

    setFiltered(temp);
    setVisibleCount(20);
  };

  const resetFilters = () => {
    setSelectedUser("");
    setTask("");
    setPractice("");
    setFromDate("");
    setToDate("");
    setFiltered(data);
    setVisibleCount(20);
  };

  const totalAchieved = useMemo(
    () => filtered.reduce((sum, r) => sum + Number(r.production_count || 0), 0),
    [filtered]
  );

  const totalHours = useMemo(
    () => filtered.reduce((sum, r) => sum + Number(r.hours_contributed || 0), 0),
    [filtered]
  );

  const uniqueUserDateCount = useMemo(() => {
    const set = new Set();

    filtered.forEach((r) => {
      const employee = String(r.user || "").trim();
      const date = r.date ? new Date(r.date).toISOString().split("T")[0] : "";
      if (employee && date) {
        set.add(`${employee}__${date}`);
      }
    });

    return set.size;
  }, [filtered]);

  const totalTarget = uniqueUserDateCount * DAILY_TARGET;

  const avgPercent = totalTarget
    ? ((totalAchieved / totalTarget) * 100).toFixed(1)
    : "0.0";

  const downloadCSV = () => {
    const headers = [
      "Date",
      "User",
      "Practice",
      "Task",
      "Achieved",
      "Hours",
      "Target",
      "Production %"
    ];

    const rows = filtered.map((r) => {
      const target = Number(r.target || DAILY_TARGET);
      const percent = target
        ? ((Number(r.production_count || 0) / target) * 100).toFixed(1)
        : "0.0";

      return [
        new Date(r.date).toLocaleDateString("en-GB"),
        r.user || "",
        r.practice || "",
        r.task || "",
        Number(r.production_count || 0),
        Number(r.hours_contributed || 0),
        target,
        `${percent}%`
      ]
        .map((value) => `"${String(value).replace(/"/g, '""')}"`)
        .join(",");
    });

    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "Manager_Report.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const visibleRows = filtered.slice(0, visibleCount);
  const hasMoreRows = visibleCount < filtered.length;

  return (
    <div style={page}>
      <div style={hero}>
        <div>
          <h2 style={title}>Manager Reports</h2>
          <p style={subtitle}>
            Review team production, hours, filters, and export performance data for your team.
          </p>
        </div>

        <button onClick={downloadCSV} style={downloadBtn}>
          <HiOutlineArrowDownTray style={{ fontSize: 16 }} />
          Download Report
        </button>
      </div>

      <div style={filterCard}>
        <div style={filterHeader}>
          <HiOutlineFunnel style={{ fontSize: 16, color: theme.primary }} />
          <span style={filterHeaderText}>Filters</span>
        </div>

        <div style={filterGrid}>
          <select
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
            style={input}
          >
            <option value="">All Users</option>
            {users.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>

          <select
            value={practice}
            onChange={(e) => setPractice(e.target.value)}
            style={input}
          >
            <option value="">All Practices</option>
            {practices.map((p) => (
              <option key={p.id} value={p.name}>
                {p.name}
              </option>
            ))}
          </select>

          <select
            value={task}
            onChange={(e) => setTask(e.target.value)}
            style={input}
          >
            <option value="">All Tasks</option>
            {tasks.map((t) => (
              <option key={t.id} value={t.name}>
                {t.name}
              </option>
            ))}
          </select>

          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={input}
          />

          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            style={input}
          />

          <div style={buttonRow}>
            <button onClick={applyFilters} style={applyBtn}>
              Apply Filters
            </button>
            <button onClick={resetFilters} style={resetBtn}>
              Reset
            </button>
          </div>
        </div>
      </div>

      <div style={summaryGrid}>
        <div style={summaryCard}>
          <p style={summaryLabel}>Total Achieved</p>
          <h3 style={summaryValue}>{totalAchieved}</h3>
        </div>

        <div style={summaryCard}>
          <p style={summaryLabel}>Total Hours</p>
          <h3 style={summaryValue}>{totalHours.toFixed(1)}</h3>
        </div>

        <div style={summaryCard}>
          <p style={summaryLabel}>Total Target</p>
          <h3 style={summaryValue}>{totalTarget}</h3>
        </div>

        <div style={summaryCard}>
          <p style={summaryLabel}>Avg Productivity</p>
          <h3 style={summaryValue}>{avgPercent}%</h3>
        </div>
      </div>

      <div style={tableCard}>
        <div style={tableHeader}>
          <div style={tableHeaderLeft}>
            <div style={iconBox}>
              <HiOutlineDocumentChartBar />
            </div>
            <div>
              <h3 style={tableTitle}>Detailed Report</h3>
              <p style={tableSubtitle}>Filtered production records for your team</p>
            </div>
          </div>
        </div>

        <table style={table}>
          <thead>
            <tr style={tableHeadRow}>
              <th style={th}>Date</th>
              <th style={th}>User</th>
              <th style={th}>Practice</th>
              <th style={th}>Task</th>
              <th style={th}>Achieved</th>
              <th style={th}>Hours</th>
              <th style={th}>Target</th>
              <th style={th}>%</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" style={{ ...td, textAlign: "center", color: theme.textMuted }}>
                  Loading...
                </td>
              </tr>
            ) : visibleRows.length === 0 ? (
              <tr>
                <td colSpan="8" style={{ ...td, textAlign: "center", color: theme.textMuted }}>
                  No records found
                </td>
              </tr>
            ) : (
              visibleRows.map((r, index) => {
                const target = Number(r.target || DAILY_TARGET);
                const percent = target
                  ? ((Number(r.production_count || 0) / target) * 100).toFixed(1)
                  : "0.0";

                return (
                  <tr
                    key={r.id}
                    style={{
                      background: index % 2 === 0 ? "#FFFFFF" : "#F8FAFC"
                    }}
                  >
                    <td style={td}>{new Date(r.date).toLocaleDateString("en-GB")}</td>
                    <td style={td}>{r.user || "-"}</td>
                    <td style={td}>{r.practice || "-"}</td>
                    <td style={td}>{r.task || "-"}</td>
                    <td style={td}>{Number(r.production_count || 0)}</td>
                    <td style={td}>{Number(r.hours_contributed || 0).toFixed(1)}</td>
                    <td style={td}>{target}</td>
                    <td style={{ ...td, color: theme.primaryDark, fontWeight: 700 }}>
                      {percent}%
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {!loading && hasMoreRows && (
          <div style={loadMoreWrap}>
            <button
              onClick={() => setVisibleCount((prev) => prev + 20)}
              style={loadMoreBtn}
            >
              Load More
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const page = {
  color: theme.text,
  fontFamily: "sans-serif"
};

const hero = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "16px",
  marginBottom: "24px",
  flexWrap: "wrap"
};

const title = {
  margin: 0,
  fontSize: "28px",
  fontWeight: 700,
  color: theme.text
};

const subtitle = {
  margin: "8px 0 0 0",
  color: theme.textMuted,
  fontSize: "14px"
};

const downloadBtn = {
  border: "none",
  borderRadius: "14px",
  padding: "12px 18px",
  background: "linear-gradient(135deg, #0F766E 0%, #14B8A6 100%)",
  color: "#fff",
  fontSize: "14px",
  fontWeight: 700,
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  cursor: "pointer",
  boxShadow: "0 10px 20px rgba(15,118,110,0.18)"
};

const filterCard = {
  background: theme.surface,
  borderRadius: "22px",
  padding: "20px",
  border: "1px solid #F1F5F9",
  boxShadow: "0 12px 28px rgba(15,23,42,0.08)",
  marginBottom: "22px"
};

const filterHeader = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  marginBottom: "16px"
};

const filterHeaderText = {
  fontWeight: 700,
  color: theme.text
};

const filterGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
  gap: "12px",
  alignItems: "center"
};

const input = {
  height: "44px",
  borderRadius: "12px",
  border: "1px solid #D1D5DB",
  padding: "0 12px",
  fontSize: "14px",
  outline: "none",
  color: theme.text,
  background: "#fff"
};

const buttonRow = {
  display: "flex",
  gap: "10px",
  flexWrap: "wrap"
};

const applyBtn = {
  flex: 1,
  minWidth: "110px",
  height: "44px",
  border: "none",
  borderRadius: "12px",
  background: theme.primary,
  color: "#fff",
  fontWeight: 700,
  cursor: "pointer"
};

const resetBtn = {
  flex: 1,
  minWidth: "110px",
  height: "44px",
  borderRadius: "12px",
  border: `1px solid ${theme.border}`,
  background: theme.surfaceSoft,
  color: theme.primaryDark,
  fontWeight: 700,
  cursor: "pointer"
};

const summaryGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: "16px",
  marginBottom: "22px"
};

const summaryCard = {
  background: theme.surface,
  borderRadius: "20px",
  padding: "18px",
  border: "1px solid #F1F5F9",
  boxShadow: "0 10px 24px rgba(15,23,42,0.06)"
};

const summaryLabel = {
  margin: 0,
  color: theme.textMuted,
  fontSize: "13px"
};

const summaryValue = {
  margin: "8px 0 0 0",
  color: theme.text,
  fontSize: "24px",
  fontWeight: 800
};

const tableCard = {
  background: theme.surface,
  borderRadius: "22px",
  padding: "20px",
  border: "1px solid #F1F5F9",
  boxShadow: "0 12px 28px rgba(15,23,42,0.08)",
  overflowX: "auto"
};

const tableHeader = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "18px"
};

const tableHeaderLeft = {
  display: "flex",
  alignItems: "center",
  gap: "12px"
};

const iconBox = {
  width: "44px",
  height: "44px",
  borderRadius: "14px",
  background: theme.surfaceSoft,
  color: theme.primary,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "20px"
};

const tableTitle = {
  margin: 0,
  fontSize: "18px",
  fontWeight: 700,
  color: theme.text
};

const tableSubtitle = {
  margin: "4px 0 0 0",
  color: theme.textMuted,
  fontSize: "13px"
};

const table = {
  width: "100%",
  borderCollapse: "collapse",
  minWidth: "780px"
};

const tableHeadRow = {
  background: "#F8FAFC"
};

const th = {
  textAlign: "left",
  padding: "12px 14px",
  fontSize: "12px",
  color: theme.textMuted,
  fontWeight: 700,
  borderBottom: "1px solid #E5E7EB"
};

const td = {
  padding: "14px",
  fontSize: "14px",
  color: theme.text,
  borderBottom: "1px solid #F1F5F9"
};

const loadMoreWrap = {
  display: "flex",
  justifyContent: "center",
  paddingTop: "18px"
};

const loadMoreBtn = {
  border: "none",
  borderRadius: "12px",
  padding: "12px 18px",
  background: theme.surfaceSoft,
  color: theme.primaryDark,
  fontWeight: 700,
  cursor: "pointer"
};

export default ManagerReports;